import { TAB_DEFAULT_BASE_NAME } from '@/features/dashboard/constants/names';
import { getUniqueDashboardName } from '@/features/dashboard/lib/getUniqueDashboardName';
import type {
  DashboardTab,
  MarketApps,
  MarketAppWithTabs,
  TileBigNumber,
  WidgetV2
} from '@/features/dashboard/types';
import type { AppThunk } from '@/store';

import {
  createDashboardDraft,
  ensureTabExistsInOtherDashboard as ensureTabAction,
  initializeDraft,
  initializeOtherDashboardDraft,
  moveAppTabToOtherDashboard as moveAppTabAction,
  moveAppToOtherDashboard as moveAppAction,
  moveTabToOtherDashboard as moveTabAction,
  moveTileToOtherDashboard as moveTileAction,
  resetDraft,
  setAllDashboardsData
} from './dashboardEdit.slice';
import {
  getTabValidationErrorMessage,
  getTileValidationErrorMessage,
  isCurrentDashboard,
  validateTabMove,
  validateTileAddition
} from './lib/dashboardEditValidation';
import { mapServerDataToDraft } from './lib/mapping';

export type ThunkResult<T = void> = { ok: true; value: T } | { ok: false; error: string };

const ok = <T>(value: T): ThunkResult<T> => ({ ok: true, value });
const fail = (error: string): ThunkResult<never> => ({ ok: false, error });

interface InitializePayload {
  dashboardName: string;
  initialDashboardName: string;
  dashboardConfig: MarketApps;
  currentDashboardTabs: DashboardTab;
  tabNames: string[];
  activeTab: number;
}

export const initializeDashboardEditThunk =
  (payload: InitializePayload): AppThunk =>
  dispatch => {
    const { dashboardName, initialDashboardName, currentDashboardTabs } = payload;
    const { draftTabs, tabDrafts } = mapServerDataToDraft(currentDashboardTabs);
    dispatch(initializeDraft({ dashboardName, initialDashboardName, draftTabs, tabDrafts }));
  };

export const createUniqueDashboardDraftThunk =
  (): AppThunk<{ dashboardName: string }> => (dispatch, getState) => {
    const state = getState().dashboardEdit;
    const existingNames = new Set(Object.keys(state.allDashboardsData));

    Object.keys(state.otherDashboardDrafts).forEach(name => existingNames.add(name));

    if (state.dashboardName) existingNames.add(state.dashboardName);
    if (state.initialDashboardName) existingNames.add(state.initialDashboardName);

    const dashboardName = getUniqueDashboardName(Array.from(existingNames));
    dispatch(createDashboardDraft({ name: dashboardName }));
    return { dashboardName };
  };

interface MoveTilePayload {
  targetDashboardName: string;
  targetTabId: string;
  tile: TileBigNumber;
}

export const moveTileToOtherDashboardThunk =
  (payload: MoveTilePayload): AppThunk<ThunkResult> =>
  (dispatch, getState) => {
    const { targetDashboardName, targetTabId, tile } = payload;
    let activeState = getState().dashboardEdit;

    if (
      !isCurrentDashboard(activeState, targetDashboardName) &&
      !activeState.otherDashboardDrafts[targetDashboardName]
    ) {
      const serverData = activeState.allDashboardsData[targetDashboardName];
      if (serverData) {
        dispatch(initializeOtherDashboardDraft({ dashboardName: targetDashboardName, serverData }));
        activeState = getState().dashboardEdit;
      }
    }

    let targetTabDraft;

    if (isCurrentDashboard(activeState, targetDashboardName)) {
      targetTabDraft = activeState.tabDrafts[targetTabId];
      if (!targetTabDraft) return fail('Target tab not found');
    } else {
      const dashboardDraft = activeState.otherDashboardDrafts[targetDashboardName] || {
        draftTabs: [],
        tabDrafts: {}
      };
      targetTabDraft = dashboardDraft.tabDrafts[targetTabId] || { marketTabApps: [], tiles: [] };
    }

    const validationError = validateTileAddition(targetTabDraft, tile);
    if (validationError) return fail(getTileValidationErrorMessage(validationError));

    dispatch(moveTileAction({ targetDashboardName, targetTabId, tile }));
    return ok(undefined);
  };

interface MoveTabPayload {
  targetDashboardName: string;
  tabId: string;
  tabName: string;
}

export const moveTabToOtherDashboardThunk =
  (payload: MoveTabPayload): AppThunk<ThunkResult> =>
  (dispatch, getState) => {
    const { targetDashboardName, tabId, tabName } = payload;
    let state = getState().dashboardEdit;

    if (isCurrentDashboard(state, targetDashboardName)) return ok(undefined);

    if (!state.otherDashboardDrafts[targetDashboardName]) {
      const serverData = state.allDashboardsData[targetDashboardName];
      if (serverData) {
        dispatch(initializeOtherDashboardDraft({ dashboardName: targetDashboardName, serverData }));
        state = getState().dashboardEdit;
      }
    }

    const targetDashboardDraft = state.otherDashboardDrafts[targetDashboardName];
    const targetTabs = targetDashboardDraft ? targetDashboardDraft.draftTabs : [];
    const effectiveTabName = tabName.trim() || TAB_DEFAULT_BASE_NAME;
    const validationError = validateTabMove(targetTabs, effectiveTabName);

    if (validationError)
      return fail(getTabValidationErrorMessage(validationError, effectiveTabName));

    const sourceContent = state.tabDrafts[tabId];
    if (!sourceContent) return ok(undefined);

    dispatch(
      moveTabAction({
        targetDashboardName,
        tabId,
        tabName: effectiveTabName,
        marketTabApps: sourceContent.marketTabApps,
        tiles: sourceContent.tiles
      })
    );
    return ok(undefined);
  };

interface MoveAppPayload {
  targetDashboardName: string;
  targetTabId: string;
  app: MarketAppWithTabs;
}

export const moveAppToOtherDashboardThunk =
  (payload: MoveAppPayload): AppThunk =>
  (dispatch, getState) => {
    const { targetDashboardName } = payload;
    const state = getState().dashboardEdit;

    if (
      !isCurrentDashboard(state, targetDashboardName) &&
      !state.otherDashboardDrafts[targetDashboardName]
    ) {
      const serverData = state.allDashboardsData[targetDashboardName];
      if (serverData) {
        dispatch(initializeOtherDashboardDraft({ dashboardName: targetDashboardName, serverData }));
      }
    }

    dispatch(moveAppAction(payload));
  };

interface MoveAppTabPayload {
  targetDashboardName: string;
  targetTabId: string;
  tab: WidgetV2;
}

export const moveAppTabToOtherDashboardThunk =
  (payload: MoveAppTabPayload): AppThunk =>
  (dispatch, getState) => {
    const { targetDashboardName } = payload;
    const state = getState().dashboardEdit;

    if (
      !isCurrentDashboard(state, targetDashboardName) &&
      !state.otherDashboardDrafts[targetDashboardName]
    ) {
      const serverData = state.allDashboardsData[targetDashboardName];
      if (serverData) {
        dispatch(initializeOtherDashboardDraft({ dashboardName: targetDashboardName, serverData }));
      }
    }

    dispatch(moveAppTabAction(payload));
  };

interface EnsureTabPayload {
  dashboardName: string;
  tabId: string;
  tabName: string;
}

export const ensureTabExistsInOtherDashboardThunk =
  (payload: EnsureTabPayload): AppThunk =>
  (dispatch, getState) => {
    const { dashboardName } = payload;
    const state = getState().dashboardEdit;

    if (!isCurrentDashboard(state, dashboardName) && !state.otherDashboardDrafts[dashboardName]) {
      const serverData = state.allDashboardsData[dashboardName];
      if (serverData) {
        dispatch(initializeOtherDashboardDraft({ dashboardName, serverData }));
      }
    }

    dispatch(ensureTabAction(payload));
  };

interface ReinitializePayload {
  dashboardName: string;
  activeTab?: number;
  allDashboardsData?: Record<string, DashboardTab>;
}

export const reinitializeDraftThunk =
  (payload: ReinitializePayload): AppThunk<ThunkResult> =>
  (dispatch, getState) => {
    const { dashboardName, allDashboardsData: newDashboardsData } = payload;

    if (newDashboardsData) {
      dispatch(setAllDashboardsData({ data: newDashboardsData }));
    }

    const state = getState().dashboardEdit;
    const serverData = newDashboardsData?.[dashboardName] ?? state.allDashboardsData[dashboardName];

    if (!serverData) {
      return fail(`Dashboard "${dashboardName}" not found in cached data`);
    }

    const { draftTabs, tabDrafts } = mapServerDataToDraft(serverData);
    dispatch(resetDraft());
    dispatch(
      initializeDraft({
        dashboardName,
        initialDashboardName: dashboardName,
        draftTabs,
        tabDrafts
      })
    );
    return ok(undefined);
  };
